// Shared client-side logic for the RAMS rejection checker: the result shape,
// the parse/derive/count helpers, the eight review dimensions and the
// fetch + Anthropic-SSE streaming call to /api/rams-check. Extracted from
// components/tools/RamsRejectionChecker.tsx so the same review can run inside
// the report builder (components/BuilderReview.tsx) without duplicating it.
// No behaviour change — the tool imports all of this unchanged.

export type DimensionKey =
  | "site_specificity"
  | "method_sequence"
  | "hierarchy_of_controls"
  | "permits_high_risk"
  | "coshh"
  | "competence"
  | "emergency_arrangements"
  | "briefing_signoff";

export type Severity = "critical" | "major" | "minor";

export interface Finding {
  severity: Severity;
  issue: string;
  fix: string;
}

export interface Dimension {
  key: DimensionKey;
  name: string;
  score: 0 | 1 | 2 | 3 | 4 | 5;
  findings: Finding[];
}

export type VerdictKey =
  | "likely_accepted"
  | "minor_revisions_needed"
  | "likely_rejected"
  | "not_a_rams_document";

export interface CheckResult {
  verdict: VerdictKey;
  summary: string;
  dimensions: Dimension[];
  revision_notes: string[];
}

/* The eight dimensions, in review order — also the progress checklist while
   streaming. The worker counts occurrences of "key" in the JSON as each
   dimension lands, so callers tick them off by count. */
export const DIMENSION_LABELS: string[] = [
  "Site specificity",
  "Method sequence",
  "Hierarchy of controls",
  "Permits & high-risk work",
  "COSHH",
  "Competence",
  "Emergency arrangements",
  "Briefing & sign-off",
];

/** Parse the accumulated JSON string into a CheckResult, tolerating any
    leading/trailing whitespace or stray prose around the object. Returns null
    if it can't be parsed into the expected shape. */
export function parseResult(raw: string): CheckResult | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    // Salvage the outermost { … } if the model wrapped the JSON in any text.
    const start = trimmed.indexOf("{");
    const end = trimmed.lastIndexOf("}");
    if (start === -1 || end <= start) return null;
    try {
      parsed = JSON.parse(trimmed.slice(start, end + 1));
    } catch {
      return null;
    }
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const obj = parsed as Partial<CheckResult>;
  if (typeof obj.verdict !== "string" || typeof obj.summary !== "string") return null;
  const dimensions = Array.isArray(obj.dimensions) ? obj.dimensions : [];
  return {
    // Derive the verdict from the finding counts rather than trusting the
    // model's own field — it sometimes returns "likely_accepted" alongside
    // major findings, contradicting the stated rubric. not_a_rams_document is
    // the only model-asserted verdict we keep.
    verdict: obj.verdict === "not_a_rams_document" ? "not_a_rams_document" : deriveVerdict(dimensions),
    summary: obj.summary,
    dimensions,
    revision_notes: Array.isArray(obj.revision_notes) ? obj.revision_notes : [],
  };
}

export function deriveVerdict(dimensions: Dimension[]): VerdictKey {
  const counts = countFindings(dimensions);
  if (counts.critical > 0) return "likely_rejected";
  if (counts.major > 0) return "minor_revisions_needed";
  return "likely_accepted";
}

export function countFindings(dimensions: Dimension[]): { critical: number; major: number; minor: number } {
  const c = { critical: 0, major: 0, minor: 0 };
  for (const d of dimensions) {
    for (const f of d.findings) c[f.severity]++;
  }
  return c;
}

/**
 * POST the document text to /api/rams-check and consume the Anthropic SSE
 * passthrough, accumulating content_block_delta text into one JSON string.
 * `onDelta` is called with the accumulated JSON so far on each delta (callers
 * use it to count "key" occurrences for the progress ticks). Resolves to the
 * parsed CheckResult. Throws Error with the server's message when the response
 * is a non-OK / JSON error body, or a generic message on connection/parse
 * failure.
 */
export async function runRamsCheck(
  text: string,
  onDelta: (accumulated: string) => void,
): Promise<CheckResult> {
  let res: Response;
  try {
    res = await fetch("/api/rams-check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
  } catch {
    throw new Error("Couldn't reach the checker — check your connection and try again.");
  }

  // Errors come back as non-streaming JSON { error }.
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok || contentType.includes("application/json")) {
    let message = "The checker is having trouble right now — try again in a minute.";
    try {
      const data = await res.json();
      if (data && typeof data.error === "string" && data.error.trim()) message = data.error;
    } catch {
      /* non-JSON error body — keep the friendly default */
    }
    throw new Error(message);
  }
  if (!res.body) throw new Error("Couldn't reach the checker — check your connection and try again.");

  // Anthropic SSE passthrough: accumulate content_block_delta text into one
  // JSON string. Buffer across chunks so a delta split mid-line survives.
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let json = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6);
        if (payload === "[DONE]") continue;
        try {
          const ev = JSON.parse(payload);
          if (ev.type === "content_block_delta" && ev.delta?.text) {
            json += ev.delta.text;
            onDelta(json);
          }
        } catch {
          /* event: ping / keepalives / non-JSON data lines */
        }
      }
    }
  } catch {
    throw new Error("Couldn't reach the checker — check your connection and try again.");
  }

  const parsed = parseResult(json);
  if (!parsed) throw new Error("Couldn't reach the checker — check your connection and try again.");
  return parsed;
}

/** HTML → reviewable plain text that keeps table structure readable. Ported
    from qa-rams-template-check.mts so the in-builder review feeds the checker
    the same shape of text the QA sweep does. */
export function htmlToText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<\/(td|th)>/gi, " | ")
    .replace(/<\/(tr|p|div|li|h[1-6])>/gi, "\n")
    .replace(/<br[^>]*>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ").replace(/&#39;|&apos;/g, "'").replace(/&quot;/g, '"')
    .replace(/[ \t]+/g, " ")
    .replace(/ ?\| ?\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
